import { useCallback, useEffect, useRef, useState } from "react";

import {
    BACKGROUND_TRACK_GAIN,
    backgroundTrackSrc,
} from "../domain/backgroundTracks";
import { BackgroundTrackId } from "../domain/types";
import { trackPlaybackUrl } from "./useMeditationTracks";

export type StudioPreview = {
    playing: boolean;
    /** Seconds into the take. */
    position: number;
    play: () => void;
    pause: () => void;
    /** 0..1 of the take's length. */
    seekTo: (fraction: number) => void;
    stop: () => void;
};

/** Often enough that the playhead moves smoothly over the bars. */
const TICK_MS = 100;

/**
 * Hearing the take the way the session will hear it.
 *
 * Two plain elements rather than a Web Audio graph: nothing here is recorded
 * or published, so the bed only has to sit under the voice at the same gain
 * the session plays it at. That gain is the one number shared with the stage,
 * so what the therapist approves in the studio is what the patient hears.
 *
 * The voice owns the clock. The bed loops under it and is never seeked — a
 * looping ambience has no position worth keeping in step with, and asking it
 * to chase one would only make the seam audible.
 */
export function useStudioPreview(
    takeUrl: string | null,
    backgroundTrack: BackgroundTrackId,
    durationSeconds: number,
    /** One of the therapist's own tracks, which needs a signed URL first. */
    backgroundTrackId: string | null = null,
    /** 0..1 each, as set in the studio's mixer. */
    voiceLevel: number = 1,
    bedLevel: number = BACKGROUND_TRACK_GAIN
): StudioPreview {
    const [playing, setPlaying] = useState(false);
    const [position, setPosition] = useState(0);

    const voiceRef = useRef<HTMLAudioElement | null>(null);
    const bedRef = useRef<HTMLAudioElement | null>(null);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopTicking = useCallback(() => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    const pause = useCallback(() => {
        voiceRef.current?.pause();
        bedRef.current?.pause();
        stopTicking();
        setPlaying(false);
    }, [stopTicking]);

    const stop = useCallback(() => {
        pause();
        if (voiceRef.current) voiceRef.current.currentTime = 0;
        setPosition(0);
    }, [pause]);

    // A new take, or a different bed, replaces what is loaded — never layers
    // onto it, which is how a therapist ends up hearing two beds at once.
    useEffect(() => {
        stopTicking();
        setPlaying(false);
        setPosition(0);

        voiceRef.current?.pause();
        voiceRef.current = takeUrl ? new Audio(takeUrl) : null;
        if (voiceRef.current) {
            voiceRef.current.volume = voiceLevel;
            voiceRef.current.onended = () => {
                stopTicking();
                setPlaying(false);
                setPosition(durationSeconds);
                bedRef.current?.pause();
            };
        }

        bedRef.current?.pause();
        bedRef.current = null;

        let cancelled = false;
        const loadBed = (src: string) => {
            if (cancelled) return;
            const bed = new Audio(src);
            bed.loop = true;
            bed.volume = bedLevel;
            bedRef.current = bed;
        };

        if (backgroundTrackId) {
            // The therapist's own music is not public, so its URL is asked for
            // rather than known. A failure costs the preview its bed, never
            // the take it is previewing.
            void trackPlaybackUrl(backgroundTrackId)
                .then(loadBed)
                .catch(() => undefined);
        } else {
            const bedSrc = backgroundTrackSrc(backgroundTrack);
            if (bedSrc) loadBed(bedSrc);
        }

        return () => {
            cancelled = true;
            stopTicking();
            voiceRef.current?.pause();
            bedRef.current?.pause();
        };
        // The levels are deliberately not in the dependencies: moving a
        // slider adjusts what is already playing rather than reloading it,
        // which would stop the preview mid-sentence.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [takeUrl, backgroundTrack, backgroundTrackId, durationSeconds, stopTicking]);

    useEffect(() => {
        if (voiceRef.current) voiceRef.current.volume = voiceLevel;
        if (bedRef.current) bedRef.current.volume = bedLevel;
    }, [voiceLevel, bedLevel]);

    const play = useCallback(() => {
        const voice = voiceRef.current;
        if (!voice) return;

        void voice.play().catch(() => undefined);
        void bedRef.current?.play().catch(() => undefined);
        setPlaying(true);

        stopTicking();
        tickRef.current = setInterval(() => {
            const current = voiceRef.current?.currentTime ?? 0;
            setPosition(Number.isFinite(current) ? current : 0);
        }, TICK_MS);
    }, [stopTicking]);

    const seekTo = useCallback(
        (fraction: number) => {
            const voice = voiceRef.current;
            if (!voice || durationSeconds <= 0) return;
            const seconds = Math.min(Math.max(fraction, 0), 1) * durationSeconds;
            voice.currentTime = seconds;
            setPosition(seconds);
        },
        [durationSeconds]
    );

    return { playing, position, play, pause, seekTo, stop };
}
